import {
  RoutineWorkflow,
  useRoutineAlerts,
  useWorkspaceAsset,
} from '@gripp/shared-logic';
import {
  AlertSummary,
  EditAlertForm,
  LoadingIndicator,
  NewAlertForm,
  SlideAction,
  SlideLayout,
  type ColumnData,
  type SlideLayoutLocationState,
  tokens,
  ViewMode,
} from '@gripp/shared-ui';
import { ScrollView, StyleSheet, View } from 'react-native';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import {
  AlertFormSlot,
  useAlertFormColumn,
} from '../../shared/components/alerts/alertFormPanel';
import { NewAlertHeaderAction } from '../../shared/components/alerts/newAlertHeaderAction';
import { AssetRouteProvider, useAssetRouting } from '../assetRouteContext';
import { AssetRoutineSubmitColumn } from './asset-routine-submit-column';
import { AssetRoutineSubmitHeader } from './asset-routine-submit-header';

type AlertFormLocationState = SlideLayoutLocationState & {
  fromRoutine?: boolean;
} & Record<string, unknown>;

const toAlertFormLocationState = (
  state: unknown
): AlertFormLocationState | null =>
  state && typeof state === 'object' && !Array.isArray(state)
    ? (state as AlertFormLocationState)
    : null;

// ─── Alert list (left column) ────────────────────────────────────────────────

type AlertListContentProps = {
  assetId: string;
  routineId: string;
};

const AlertListContent = ({ assetId, routineId }: AlertListContentProps) => {
  const navigate = useNavigate();
  const { basePath } = useAssetRouting(assetId);
  const { workspaceAsset: rawWorkspaceAsset } = useWorkspaceAsset(assetId);
  // Use the enriched workspaceAsset from useRoutineAlerts (carries workflowState,
  // which AlertSummary needs for lastTriggered / lastCount). Passing the raw
  // useWorkspaceAsset value here drops workflowState, so the summary computes the
  // "no prior alert" branch and shows wrong Next/Currently/Last Alert values —
  // mirror AlertPanel, which already does this correctly.
  const {
    routineWorkflows,
    workspaceAsset,
    isLoading,
  } = useRoutineAlerts(rawWorkspaceAsset?.id ?? '', routineId, undefined, {
    enabled: !!rawWorkspaceAsset?.id,
  });

  const handleAlertSelected = (selectedAlert: RoutineWorkflow) => {
    if (!selectedAlert?.id) {
      return;
    }

    navigate(
      `${basePath}/routines/${routineId}/alerts/${selectedAlert.id}/edit`
    );
  };

  if (isLoading || !workspaceAsset) {
    return <LoadingIndicator flexContainer />;
  }

  return (
    <View style={listStyles.container}>
      <ScrollView>
        {routineWorkflows?.map((item: RoutineWorkflow, index: number) => (
          <AlertSummary
            key={`${index}-${item.id}`}
            workflow={item}
            workspaceAsset={workspaceAsset}
            routineId={routineId}
            onAlertSelected={handleAlertSelected}
          />
        ))}
      </ScrollView>
    </View>
  );
};

// ─── Form content (right column) ─────────────────────────────────────────────

type AlertFormContentProps = AlertFormSlot & {
  assetId: string;
  routineId: string;
  alertId?: string;
  onBack: () => void;
};

const AlertFormContent = ({
  assetId,
  routineId,
  alertId,
  onBack,
  formRef,
  renderBottomComponent,
  onRegisterBackGuard,
}: AlertFormContentProps) => {
  const { workspaceAsset } = useWorkspaceAsset(assetId);

  if (!workspaceAsset) return null;

  return alertId ? (
    <EditAlertForm
      key={alertId}
      alertId={alertId}
      routineId={routineId}
      workspaceAssetId={workspaceAsset.id}
      onClose={onBack}
      formRef={formRef}
      renderBottomComponent={renderBottomComponent}
      onRegisterBackGuard={onRegisterBackGuard}
    />
  ) : (
    <NewAlertForm
      routineId={routineId}
      workspaceAssetId={workspaceAsset.id}
      onClose={onBack}
      formRef={formRef}
      renderBottomComponent={renderBottomComponent}
      onRegisterBackGuard={onRegisterBackGuard}
    />
  );
};

// ─── Page ─────────────────────────────────────────────────────────────────────

export const AssetAlertFormPage = () => {
  const { assetId, routineId, alertId, issueId } = useParams<{
    assetId: string;
    routineId: string;
    alertId?: string;
    issueId?: string;
  }>();
  const navigate = useNavigate();
  const location = useLocation();

  const basePath = issueId
    ? `/activities/${issueId}/asset/${assetId}`
    : `/assets/${assetId}`;
  const routineUrl = `${basePath}/routines/${routineId}`;
  const locationState = toAlertFormLocationState(location.state);
  const initialTransition =
    locationState?.slideLayoutInitialTransition === SlideAction.NEXT ||
    locationState?.slideLayoutInitialTransition === SlideAction.PREV
      ? locationState.slideLayoutInitialTransition
      : undefined;
  // Set by AssetRoutineSubmitHeader when the routine has no alerts yet —
  // signals that the actual previous step is the routine submit page, not
  // the (empty) alerts list, so the left column should mirror that.
  const fromRoutine = locationState?.fromRoutine;
  // Preserve the rest of the state (e.g. RoutineSubmitSource `from`) when
  // going back so AssetRoutineSubmit doesn't fall back to its AssetCard
  // default. `fromRoutine` itself is dropped — it only applies to this hop.
  const backState = (() => {
    if (!locationState) return undefined;
    const {
      fromRoutine: _dropFromRoutine,
      slideLayoutInitialTransition: _dropInitialTransition,
      ...rest
    } = locationState;
    return Object.keys(rest).length > 0 ? rest : undefined;
  })();
  const getBackStateWithTransition = (transition: SlideAction) => ({
    ...(backState ?? {}),
    slideLayoutInitialTransition: transition,
  });

  const rightColumn = useAlertFormColumn({
    title: alertId ? 'alerts.editAlert' : 'alerts.newAlert',
    // Arriving straight from the routine page (no alerts yet), back skips the
    // empty alerts list and returns to the routine.
    onBack: () =>
      navigate(fromRoutine ? routineUrl : `${routineUrl}/alerts`, {
        state: getBackStateWithTransition(SlideAction.PREV),
      }),
    renderForm: (slot) => (
      <AlertFormContent
        {...slot}
        assetId={assetId ?? ''}
        routineId={routineId ?? ''}
        alertId={alertId}
        onBack={() => navigate(routineUrl, { state: backState })}
      />
    ),
    column: {
      backButtonTransition: SlideAction.PREV,
      backButtonTransitionBehavior: 'concurrent',
    },
  });

  if (!assetId || !routineId) return null;

  const leftColumn: ColumnData = fromRoutine
    ? {
        title: 'routines.editRoutines.editRoutinesButton',
        mainComponent: <AssetRoutineSubmitColumn assetId={assetId} />,
        headerComponent: (
          <AssetRoutineSubmitHeader assetId={assetId} routineId={routineId} />
        ),
        showBackButton: true,
        backButtonTransition: SlideAction.PREV,
        backButtonTransitionBehavior: 'concurrent',
        backButtonTransitionExitColumn: 'both',
        onBackButtonPress: () =>
          navigate(routineUrl, {
            state: getBackStateWithTransition(SlideAction.PREV),
          }),
        disableScroll: true,
      }
    : {
        title: 'alerts.alerts',
        mainComponent: (
          <AlertListContent assetId={assetId} routineId={routineId} />
        ),
        headerComponent: <NewAlertHeaderAction />,
        showBackButton: true,
        backButtonTransition: SlideAction.PREV,
        backButtonTransitionBehavior: 'concurrent',
        backButtonTransitionExitColumn: 'both',
        onBackButtonPress: () =>
          navigate(routineUrl, {
            state: getBackStateWithTransition(SlideAction.PREV),
          }),
        disableScroll: true,
      };

  return (
    <AssetRouteProvider
      value={{
        basePath,
        listPath: issueId ? '/activities' : '/assets',
        isWorkspaceMode: false,
        issueId: issueId ?? undefined,
      }}
    >
      <SlideLayout
        leftColumn={leftColumn}
        rightColumn={rightColumn}
        viewMode={ViewMode.DUAL}
        initialTransition={initialTransition}
      />
    </AssetRouteProvider>
  );
};

// ─── Styles ───────────────────────────────────────────────────────────────────

const listStyles = StyleSheet.create({
  container: {
    flex: 1,
    margin: tokens.spacing.xl,
  },
});

